import request from '@/utils/request'
import {
  BasePriceListReq,
  BasePriceListRes,
  BasePriceTimelineRes,
  CodeName,
  CategoryPriceCreateReq,
  CategoryPriceListReq,
  CategoryPriceListRes,
  CategoryPriceUpdateReq,
  PatchDeleteCategoryPriceRes,
  PatchEndCategoryPriceRes,
  PriceCodesRes,
  PriceExcelUploadRes,
} from '@/services/settlement/settlementPricing/types'
import { omit } from 'lodash-es'

const BASE = '/api2/mass/v1/prices'

// 카테고리 단가 엑셀 전체 다운로드 (POST·검색 조건 매칭 전체·.xlsx)
export const CATEGORY_EXCEL_DOWNLOAD_URL = `${BASE}/category/excel-download`

// 엑셀 템플릿(업로드 양식) 다운로드는 BE 엔드포인트 대신 public 정적 .xlsx 를 사용한다.
// (utils/template.ts → downloadCategoryPriceTemplate / public/MFS_카테고리단가_업로드양식.xlsx)

// 엑셀 업로드 실패 응답 코드 (FR-PM12 — settlementManagement 엑셀 업로드 컨벤션 동일)
export const PRICE_EXCEL_ERROR_CODES = {
  EXCEL_ROW_COUNT_LIMIT_EXCEEDED: 'EXCEL_ROW_COUNT_LIMIT_EXCEEDED', // 1,000건 초과
  EXCEL_PARTIAL_VALIDATION_FAILURE: 'EXCEL_PARTIAL_VALIDATION_FAILURE', // 일부 행 오류 (message=실패 행 번호 콤마)
  EXCEL_TOTAL_VALIDATION_FAILURE: 'EXCEL_TOTAL_VALIDATION_FAILURE', // 전체 행 오류
  INVALID_EXCEL_FILE: 'INVALID_EXCEL_FILE', // 양식 오류
} as const

export type PriceExcelErrorCode = (typeof PRICE_EXCEL_ERROR_CODES)[keyof typeof PRICE_EXCEL_ERROR_CODES]

// ── 공통 코드 ──────────────────────────────────
export const getPriceCodes = async () => await request.get<ApiResponseV2<PriceCodesRes>>(`${BASE}/codes`)

// ── 탭① 기본단가 (PriceController /base) — 읽기 전용 마스터 ──────────
export const searchBasePrices = async (params: ApiPaginationReq<BasePriceListReq>) =>
  await request.post<ApiPaginationV2<BasePriceListRes>, ApiPaginationReq<BasePriceListReq>>(`${BASE}/base/search`, params)

// 기본단가 변경 이력(조회전용) — 행 클릭 모달
export const getBasePriceTimeline = async (masterId: number) =>
  await request.get<ApiResponseV2<BasePriceTimelineRes>>(`${BASE}/base/${masterId}`)

// bizest 대분류 필터 옵션 (category_depth_1 고유 목록)
export const getBizestCategories = async () =>
  await request.get<ApiResponseV2<{ items: CodeName[] }>>(`${BASE}/category/bizest-categories`)

// ── 탭② 카테고리 단가 (PriceController /category) ──
export const searchCategoryPrices = async (params: ApiPaginationReq<CategoryPriceListReq>) =>
  await request.post<ApiPaginationV2<CategoryPriceListRes>, ApiPaginationReq<CategoryPriceListReq>>(`${BASE}/category/search`, params)

export const createCategoryPrice = async (params: CategoryPriceCreateReq) =>
  await request.post<ApiResponseV2<CategoryPriceListRes>, CategoryPriceCreateReq>(`${BASE}/category`, params)

export const updateCategoryPrice = async (params: CategoryPriceUpdateReq) =>
  await request.put<ApiResponseV2<CategoryPriceListRes>, Omit<CategoryPriceUpdateReq, 'id'>>(`${BASE}/category/${params.id}`, omit(params, 'id'))

// 다중선택 일괄종료
export const endCategoryPrices = async (params: { ids: number[] }) =>
  await request.patch<ApiResponseV2<PatchEndCategoryPriceRes>, { ids: number[] }>(`${BASE}/category/end`, params)

// 다중선택 일괄삭제 (대기 상태만 하드 삭제)
export const deleteCategoryPrices = async (params: { ids: number[] }) =>
  await request.post<ApiResponseV2<PatchDeleteCategoryPriceRes>, { ids: number[] }>(`${BASE}/category/delete`, params)

// ── 엑셀 일괄 업로드 (multipart·all-or-nothing) ──
export const validatePriceExcel = async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return await request.post<ApiResponseV2<boolean>, FormData>(`${BASE}/category/excel-validation`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const uploadPriceExcel = async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return await request.post<ApiResponseV2<PriceExcelUploadRes>, FormData>(`${BASE}/category/excel-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
